import React, { useEffect, useId, useState } from 'react';
import { FOUNDERS_GOLD, type EditionTheme, type ProductImage } from '../../products/editions';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

export const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(() => window.matchMedia(REDUCED_MOTION).matches);
  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION);
    const onChange = (): void => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

/**
 * Scrolls a section into view and moves focus to it, so keyboard and screen-reader users land
 * where sighted users do. Targets carry tabIndex={-1} and a scroll margin clearing the sticky nav.
 * Focus goes first: focusing during a smooth scroll cancels the scroll in Chrome.
 */
export const scrollToSection = (id: string): void => {
  const target = document.getElementById(id);
  if (!target) return;
  target.focus({ preventScroll: true });
  target.scrollIntoView({ behavior: window.matchMedia(REDUCED_MOTION).matches ? 'auto' : 'smooth', block: 'start' });
};

const [goldR, goldG, goldB] = [1, 3, 5].map((i) => (parseInt(FOUNDERS_GOLD.slice(i, i + 2), 16) / 255).toFixed(3));

/**
 * The official logo (public/logo.png) in monochromatic gold. It is the same file recoloured by an
 * SVG filter, not a redrawing: every coloured pixel becomes gold, the white ground and the white
 * cat-and-dog cut-outs become transparent, and the original alpha still bounds the result.
 */
export const GoldLogo: React.FC<{ className?: string; alt?: string }> = ({ className = '', alt = '' }) => {
  const filterId = `pf-gold-${useId().replace(/:/g, '')}`;
  return (
    <>
      <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
        <filter id={filterId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values={`0 0 0 0 ${goldR}  0 0 0 0 ${goldG}  0 0 0 0 ${goldB}  -2 -2 -2 0 5.6`}
          />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>
      </svg>
      <img src="/logo.png" alt={alt} className={className} style={{ filter: `url(#${filterId})` }} />
    </>
  );
};

export const StatusChip: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${className}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
    {children}
  </span>
);

interface ProductImageFrameProps {
  readonly image: ProductImage | null;
  /** Names the pending image in the placeholder. */
  readonly label: string;
  readonly theme: EditionTheme;
  readonly gold?: boolean;
  /** Aspect-ratio utility; the frame reserves its box before the image loads, so nothing shifts. */
  readonly aspect?: string;
  readonly eager?: boolean;
  readonly className?: string;
}

export const ProductImageFrame: React.FC<ProductImageFrameProps> = ({
  image,
  label,
  theme,
  gold = false,
  aspect = 'aspect-[4/3]',
  eager = false,
  className = '',
}) => (
  <div className={`relative w-full overflow-hidden rounded-xl ${aspect} ${theme.surface} ${className}`}>
    {image ? (
      <>
        <img
          src={image.src}
          srcSet={image.srcSet}
          sizes="(min-width: 1024px) 50vw, 100vw"
          alt={image.alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain"
        />
        {image.concept && (
          <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Concept render
          </span>
        )}
      </>
    ) : (
      <div
        className={`absolute inset-3 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-4 text-center ${
          theme.dark ? 'border-white/25' : 'border-gray-300'
        }`}
      >
        {gold ? (
          <GoldLogo className="h-16 w-16 sm:h-20 sm:w-20" />
        ) : (
          <img src="/logo.png" alt="" className="h-14 w-14 sm:h-16 sm:w-16 rounded-full opacity-80" />
        )}
        <p className={`text-sm font-semibold ${theme.ink}`}>{label}</p>
        <p className={`text-xs ${theme.muted}`}>Product image coming soon</p>
      </div>
    )}
  </div>
);
